/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import {
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  SectionList,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { settingsStyles as styles } from '../../../settings/screens/settingsScreen.styles';
import { ShellContentFrame } from '../../../shared/layout/ShellContentFrame'
import { ShellScreenLoading } from '../../../shared/layout/ShellScreenLoading'
import { useContentLayout } from '../../../shared/layout/useContentLayout';
import { spacing } from '../../../shared/theme/spacing'
import { webOperationalScrollBindings } from '../../../shared/theme/webScrollbar';
import { ensureTicketFonts } from '../../../shared/ticket/ensureTicketFonts'
import { ticketColors } from '../../../shared/theme/ticketColors'
import { TicketModuleHeader } from '../../../shared/ticket/TicketModuleHeader';
import { ModuleEmptyState } from '../../../shared/ui/ModuleEmptyState';
import type { CashSessionRow } from '../../infra/cashSessionsRepo';
import { useCashManagement } from '../useCashManagement';
import { formatLocalDateTime, formatRelativeDayTime } from '@shared/vueltoDateTime';
import { useScreenLoadShell } from '../../../shared/ui/hooks-screen/useScreenLoad';
import { CashOutflowsSection } from '../CashOutflowsSection';
import { ChecksPortfolioSection } from '../ChecksPortfolioSection';
import { CashOutflowRegisterModal } from '../CashOutflowRegisterModal';
import { ExpenseRegisterModal } from '../../../expenses/components/ExpenseRegisterModal';
import { CashActivityFeed } from '../CashActivityFeed';
import { CashCloseTurnoModal } from '../CashCloseTurnoModal';
import { CashModeChrome } from './CashModeChrome';
import { CashTurnoHeroPanel } from './CashTurnoHeroPanel';
import { CashOperationalSheetModal } from './CashOperationalSheetModal';
import { CashRecordHeader } from './CashRecordHeader';
import { CashWorkspaceSectionToggle } from './CashWorkspaceSectionToggle';
import { CashHistorialAuditPanel } from './CashHistorialAuditPanel';
import { CashOpenTurnoForm } from './CashOpenTurnoForm';
import { CashTurnoWorkspacePanel } from './CashTurnoWorkspacePanel';
import { CashWorkspaceTabPane } from './CashWorkspaceTabPane';
import { CashHistorialFiltersBlock } from './CashHistorialFiltersBlock';
import { FilterSheetButton } from '../../../shared/filters/FilterSheetButton'
import { FilterSheetModal } from '../../../shared/filters/FilterSheetModal';
import { CashHistoryPagination } from './CashHistoryPagination';
import { CashCustomRangeModal } from './CashCustomRangeModal';
import {
  CashSessionDetailPanel,
  CashLastClosedSummary,
} from './CashDetailPanels';
import { HistorySessionRow } from './CashHistorialList';
import { buildHistorySections, formatMoney } from './cashManagement.utils';
import type { CashScreenMode, CashWorkspaceSection, HistorySection } from './cashManagement.types';
import { cashStyles } from './cashManagement.styles';
import { cashArqueoCopy } from './cashArqueoCopy';
import { cashPanelStyles as panelStyles } from './cashManagement.panelStyles';
import { posSharedUi as posUi } from '../../../shared/ui/posSharedUi';

export function CashWorkspaceScreen() {
  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const vm = useCashManagement();
  const { refreshing, onRefresh } = useScreenLoadShell(
    'CashManagement',
    async (opts) => { await vm.loadFn(opts); }
  );
  const { layoutWide, layoutWebShell } = useContentLayout('dataWide');
  const useCashWorkspace = layoutWebShell;

  const [detailSession, setDetailSession] = useState<CashSessionRow | null>(null);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [historialFiltersOpen, setHistorialFiltersOpen] = useState(false);
  const [lastClosedModalOpen, setLastClosedModalOpen] = useState(false);
  const [closeCashModalOpen, setCloseCashModalOpen] = useState(false);
  const [closeCashPreparing, setCloseCashPreparing] = useState(false);
  const [outflowsModalOpen, setOutflowsModalOpen] = useState(false);
  const [branchActivityOpen, setBranchActivityOpen] = useState(false);
  const [workspaceSection, setWorkspaceSection] = useState<CashWorkspaceSection>('operacion');
  const [openTurnoModalOpen, setOpenTurnoModalOpen] = useState(false);

  const hasActiveSession = Boolean(vm.cashSummary?.activeSession);
  const [screenMode, setScreenMode] = useState<CashScreenMode>('turno');
  const prevActiveRef = useRef(hasActiveSession);
  useEffect(() => {
    if (!prevActiveRef.current && hasActiveSession && !useCashWorkspace) {
      setScreenMode('turno');
    }
    if (hasActiveSession && openTurnoModalOpen) {
      setOpenTurnoModalOpen(false);
    }
    prevActiveRef.current = hasActiveSession;
  }, [hasActiveSession, useCashWorkspace, openTurnoModalOpen]);

  const sections = useMemo(
    () => buildHistorySections(vm.cashHistory, vm.historySort),
    [vm.cashHistory, vm.historySort]
  );
  const onOpenCustomModal = useCallback(() => {
    setHistorialFiltersOpen(false);
    setCustomModalOpen(true);
  }, []);

  const onHistoryRowPress = useCallback((item: CashSessionRow) => {
    setDetailSession(item);
  }, []);

  const outflowsSectionProps = useMemo(() => ({
    vm: vm.outflowsFlow,
    canEdit: vm.canEdit,
    canVoid: vm.canShowOutflowVoid,
    sessionOutflowsTotal: vm.cashSummary?.sessionOutflowsTotal ?? 0,
    operatorLabel: vm.resolveOperatorLabel,
    layoutWide,
    expensesModuleEnabled: vm.expensesModuleEnabled,
    expenseRegisterSuppliers: vm.expenseRegisterSuppliers,
    activeCashSessionInfo: vm.activeCashSessionInfo,
    expenseRegisterSubmitting: vm.expenseRegisterSubmit.submitting,
    onExpenseRegisterSubmit: vm.expenseRegisterSubmit.handleSubmit,
    onOpenRegister: vm.handleOpenRegisterOutflow,
    onOpenRegisterInflow: vm.handleOpenRegisterInflow,
  }), [vm, layoutWide]);

  const cashRefreshControl = useMemo(
    () => (
      <RefreshControl
        refreshing={refreshing}
        onRefresh={onRefresh}
        colors={[ticketColors.gold]}
        tintColor={ticketColors.gold}
      />
    ),
    [refreshing, onRefresh]
  );

  const renderSectionHeader = useCallback(
    ({ section }: { section: HistorySection }) =>
      section.title === '' ? null : (
        <View
          style={[
            panelStyles.historialSectionHeader,
            section.isFirstSection ? panelStyles.historialSectionHeaderFirst : panelStyles.historialSectionHeaderNext,
          ]}
        >
          <Text style={panelStyles.sectionLabelMobile}>{section.title}</Text>
        </View>
      ),
    []
  );

  const renderItem = useCallback(
    ({ section, item, index }: { section: HistorySection; item: CashSessionRow; index: number }) => {
      const isLast = index === section.data.length - 1;
      const group: 'grouped' | 'groupedLast' = isLast ? 'groupedLast' : 'grouped';
      return (
        <HistorySessionRow
          s={item}
          onPress={() => onHistoryRowPress(item)}
          operatorLabel={vm.resolveOperatorLabel}
          group={group}
        />
      );
    },
    [onHistoryRowPress, vm.resolveOperatorLabel]
  );

  const historyListFooter = (
    <>
      <CashHistoryPagination vm={vm} mobile={!useCashWorkspace} />
      {!useCashWorkspace && vm.canView ? (
        <TouchableOpacity
          style={cashStyles.branchActivityLink}
          onPress={() => setBranchActivityOpen(true)}
          accessibilityRole="button"
          accessibilityLabel="Actividad de sucursal"
        >
          <View style={cashStyles.linkTextCol}>
            <Text style={cashStyles.linkTitle}>Actividad de sucursal</Text>
            <Text style={cashStyles.linkSubtitle}>Últimos 30 días · toda la sucursal</Text>
          </View>
          <Ionicons name="chevron-forward" size={18} color={ticketColors.ink2} />
        </TouchableOpacity>
      ) : null}
    </>
  );

  const mobileListHeader =
    !useCashWorkspace && vm.canView && screenMode === 'historial' ? (
      <View style={{ gap: 10, paddingTop: spacing.sm }}>
        <TicketModuleHeader
          kicker="Caja"
          title={cashArqueoCopy.historyTitle}
          meta="Cierres de los últimos 30 días."
          testID="caja.history.header.sheet"
          tools={
            <FilterSheetButton
              activeCount={
                (vm.historyRangePreset !== '30d' ? 1 : 0) +
                (vm.differenceFilter !== 'all' ? 1 : 0) +
                (vm.operatorFilter !== 'all' ? 1 : 0) +
                (vm.historySort !== 'closed_desc' ? 1 : 0)
              }
              onOpen={() => setHistorialFiltersOpen(true)}
              accessibilityLabel="Filtrar historial"
              variant="desk"
            />
          }
        />
      </View>
    ) : null;

  const historyList = vm.canView ? (
    <SectionList
      {...webOperationalScrollBindings(useCashWorkspace ? panelStyles.workspaceListScroll : [styles.container, { width: '100%' }])}
      sections={sections}
      keyExtractor={(item) => item.id}
      renderItem={renderItem}
      renderSectionHeader={renderSectionHeader}
      keyboardShouldPersistTaps="handled"
      stickySectionHeadersEnabled={false}
      ListHeaderComponent={
        <>
          {mobileListHeader}
          {vm.historyError ? (
            <ModuleEmptyState
              variant="nest"
              title="No pudimos cargar el historial"
              subtitle="Revisá la conexión y probá de nuevo."
              showMascot={false}
              secondaryAction={{
                label: 'Reintentar',
                onPress: () => void vm.reloadHistoryFromStart(),
                testID: 'caja.history.retry',
              }}
              testID="caja.history.error"
            />
          ) : null}
        </>
      }
      ListFooterComponent={historyListFooter}
      ListEmptyComponent={
        vm.historyLoading && vm.cashHistory.length === 0 ? (
          <ModuleEmptyState
            loading
            loadingText="Cargando historial…"
            testID="caja.history.empty.loading"
          />
        ) : vm.historyError ? null : !vm.historyLoading && vm.cashHistory.length === 0 ? (
          <ModuleEmptyState
            variant="nest"
            title={
              vm.differenceFilter !== 'all' || vm.operatorFilter !== 'all'
                ? 'Sin turnos con estos filtros'
                : 'Sin cierres en este período'
            }
            subtitle={
              vm.lastClosedOutsideHistoryRange && vm.cashSummary?.lastClosedSession?.closedAt
                ? `El último cierre fue el ${formatLocalDateTime(vm.cashSummary.lastClosedSession.closedAt)}. Probá 90 días o un rango personalizado.`
                : 'Ampliá el rango de fechas o ajustá los filtros.'
            }
            kicker="Siguiente paso"
            testID="caja.history.empty"
          />
        ) : null
      }
      contentContainerStyle={
        useCashWorkspace
          ? panelStyles.sectionListContentWorkspace
          : { flexGrow: 1, paddingBottom: spacing.xxl, paddingHorizontal: spacing.md, alignSelf: 'stretch', width: '100%' }
      }
      refreshControl={cashRefreshControl}
    />
  ) : null;

  const readOnly = vm.canView && !vm.canEdit;
  const showWorkspaceEgresos = vm.cashOutflowsEnabled && Boolean(vm.cashSummary?.activeSession);
  const showCartera = vm.checksModuleEnabled && vm.canViewChecks;

  const activityScopeCaption = useMemo(() => {
    const { items, loading } = vm.activityFlow;
    if (loading && items.length === 0) return 'Cargando…';
    return 'Últimos 30 días';
  }, [vm.activityFlow]);

  const cierresNeedsAttention = vm.differencesSummary.length > 0;

  const openCloseCash = useCallback(() => {
    setCloseCashModalOpen(true);
    setCloseCashPreparing(true);
    void vm.prepareCloseCashModal().finally(() => setCloseCashPreparing(false));
  }, [vm.prepareCloseCashModal]);

  useEffect(() => {
    // El refetch de cashSummary puede pasar por estados intermedios sin
    // activeSession — no resetear el tab hasta que la condición sea estable.
    const unavailable =
      (workspaceSection === 'egresos' && !(vm.cashOutflowsEnabled && vm.cashSummary?.activeSession)) ||
      (workspaceSection === 'cartera' && !showCartera);
    if (!unavailable) return;
    const timer = setTimeout(() => setWorkspaceSection('operacion'), 800);
    return () => clearTimeout(timer);
  }, [workspaceSection, vm.cashOutflowsEnabled, vm.cashSummary?.activeSession, showCartera]);

  if (!vm.currentBranchId) {
    return (
      <View style={[styles.container, styles.loadingWrap]}>
        <ModuleEmptyState
          variant="nest"
          kicker="Caja"
          title="Elegí una sucursal"
          subtitle="Elegila en el encabezado para ver los turnos."
        />
      </View>
    );
  }

  if (vm.initialLoading) {
    return (
      <View
        testID="cash-workspace-loading"
        style={[
          panelStyles.cashWorkspaceRoot,
          Platform.OS === 'web'
            ? ({ minHeight: '100%' as unknown as number, height: '100%' as unknown as number } as const)
            : null,
        ]}
      >
        <ShellScreenLoading label="Cargando estado de caja…" />
      </View>
    );
  }

  return (
    <>
      {useCashWorkspace ? (
        <ShellContentFrame style={cashStyles.workspaceRoot} testID="cash-workspace">
          <View style={cashStyles.recordHeaderShell}>
            <CashRecordHeader
              vm={vm}
              onOpenCloseCash={openCloseCash}
              onRegisterOutflow={vm.handleOpenRegisterOutflow}
              onRegisterInflow={vm.handleOpenRegisterInflow}
              onOpenCash={() => setOpenTurnoModalOpen(true)}
            />
          </View>
          {vm.canView || showWorkspaceEgresos || showCartera ? (
            <View style={cashStyles.workspaceSectionToggleWrap}>
            <CashWorkspaceSectionToggle
              activeSection={workspaceSection}
              onChangeSection={setWorkspaceSection}
              showCierres={vm.canView}
              showActividad={vm.canView}
              showEgresos={showWorkspaceEgresos}
              showCartera={showCartera}
              cierresNeedsAttention={cierresNeedsAttention}
            />
            </View>
          ) : null}
          <View style={cashStyles.workspaceContentPane}>
            {workspaceSection === 'operacion' ? (
              <ScrollView
                {...webOperationalScrollBindings(cashStyles.workspaceTabScroll)}
                contentContainerStyle={cashStyles.workspaceTabScrollContent}
                keyboardShouldPersistTaps="handled"
                refreshControl={cashRefreshControl}
              >
                <CashWorkspaceTabPane>
                  <CashTurnoWorkspacePanel
                    vm={vm}
                    layoutWide={layoutWide}
                    embeddedScroll
                    recordPageMode
                    onOpenMovements={
                      showWorkspaceEgresos ? () => setWorkspaceSection('egresos') : undefined
                    }
                    onOpenActivity={
                      vm.canView ? () => setWorkspaceSection('actividad') : undefined
                    }
                    onOpenLastClosed={() => setLastClosedModalOpen(true)}
                  />
                </CashWorkspaceTabPane>
              </ScrollView>
            ) : null}
            {workspaceSection === 'cierres' && vm.canView ? (
              <CashHistorialAuditPanel
                vm={vm}
                onOpenCustomModal={onOpenCustomModal}
                layoutWide={layoutWide}
                compactFilters={!layoutWide}
                onOpenFiltersModal={() => setHistorialFiltersOpen(true)}
              />
            ) : null}
            {workspaceSection === 'actividad' && vm.canView ? (
              <ScrollView
                {...webOperationalScrollBindings(cashStyles.workspaceTabScroll)}
                contentContainerStyle={cashStyles.workspaceTabScrollContent}
                keyboardShouldPersistTaps="handled"
                refreshControl={cashRefreshControl}
              >
                <CashWorkspaceTabPane caption={activityScopeCaption}>
                  <View style={cashStyles.workspaceTabCardFlush}>
                    <CashActivityFeed
                      vm={vm.activityFlow}
                      operatorLabel={vm.resolveOperatorLabel}
                      layoutWide={layoutWide}
                    />
                  </View>
                </CashWorkspaceTabPane>
              </ScrollView>
            ) : null}
            {workspaceSection === 'cartera' && showCartera ? (
              <ScrollView
                {...webOperationalScrollBindings(cashStyles.workspaceTabScroll)}
                contentContainerStyle={cashStyles.workspaceTabScrollContent}
                keyboardShouldPersistTaps="handled"
                refreshControl={cashRefreshControl}
              >
                <CashWorkspaceTabPane>
                  <ChecksPortfolioSection
                    flow={vm.checksFlow}
                    branchId={vm.currentBranchId ?? null}
                    canEdit={vm.canEditChecks}
                    canEndorse={vm.canEndorseChecks}
                    canReject={vm.canRejectChecks}
                    expensesModuleEnabled={vm.expensesModuleEnabled}
                    workspaceMode
                  />
                </CashWorkspaceTabPane>
              </ScrollView>
            ) : null}
            {workspaceSection === 'egresos' && showWorkspaceEgresos ? (
              <ScrollView
                {...webOperationalScrollBindings(cashStyles.workspaceTabScroll)}
                contentContainerStyle={cashStyles.workspaceTabScrollContent}
                keyboardShouldPersistTaps="handled"
                refreshControl={cashRefreshControl}
              >
                <CashWorkspaceTabPane caption="Egresos e ingresos del turno en curso">
                  <View style={cashStyles.workspaceTabCard}>
                    <CashOutflowsSection
                      {...outflowsSectionProps}
                      leadSection
                    />
                  </View>
                </CashWorkspaceTabPane>
              </ScrollView>
            ) : null}
            {/* Caja cerrada (proto 1b, opción CTA+modal): la apertura abre
                como modal desde el CTA "Abrir caja" del header. In-page (no
                RN Modal portal) para conservar los testIDs dentro del scope
                cash-workspace que usan los specs E2E. Sin gatear por
                workspaceSection: si el modal quedó "abierto" en Turno y el
                usuario cambia de tab, el CTA debe seguir trayéndolo. */}
            {!hasActiveSession && openTurnoModalOpen ? (
              <View
                style={panelStyles.openModalBackdrop}
                testID="caja.open.modal-backdrop"
              >
                <CashOpenTurnoForm
                  vm={vm}
                  chrome="modal"
                  onClose={() => setOpenTurnoModalOpen(false)}
                  footer={
                    vm.cashOutflowsEnabled && vm.expensesModuleEnabled ? (
                      <Text style={styles.caption}>{cashArqueoCopy.closedCashExpensesHint}</Text>
                    ) : null
                  }
                />
              </View>
            ) : null}
          </View>
        </ShellContentFrame>
      ) : screenMode === 'turno' ? (
        <ShellContentFrame style={{ width: '100%' }} testID="cash-workspace">
          <CashModeChrome
            mode={screenMode}
            onModeChange={setScreenMode}
            readOnly={readOnly}
            showHistorial={vm.canView}
            showCartera={showCartera}
            cierresNeedsAttention={cierresNeedsAttention}
          />
          <ScrollView
            {...webOperationalScrollBindings(cashStyles.turnoModeScroll)}
            contentContainerStyle={cashStyles.turnoModeContent}
            keyboardShouldPersistTaps="handled"
            refreshControl={cashRefreshControl}
          >
            <CashRecordHeader
              vm={vm}
              onOpenCloseCash={openCloseCash}
              onRegisterOutflow={vm.handleOpenRegisterOutflow}
              onRegisterInflow={vm.handleOpenRegisterInflow}
            />
            <CashTurnoHeroPanel
              vm={vm}
              onOpenLastClosed={() => setLastClosedModalOpen(true)}
              onOpenOutflows={() => setOutflowsModalOpen(true)}
              onOpenCloseCash={openCloseCash}
              onRegisterOutflow={vm.handleOpenRegisterOutflow}
              onRegisterInflow={vm.handleOpenRegisterInflow}
              // Paridad con el tab Actividad de desktop: el link solo aparece
              // con permiso de vista (canView), no con cash_outflows.
              onOpenActivity={vm.canView ? () => setBranchActivityOpen(true) : undefined}
            />
          </ScrollView>
        </ShellContentFrame>
      ) : screenMode === 'cartera' && showCartera ? (
        <ShellContentFrame style={{ width: '100%' }} testID="cash-cartera">
          <CashModeChrome
            mode={screenMode}
            onModeChange={setScreenMode}
            readOnly={readOnly}
            showHistorial={vm.canView}
            showCartera={showCartera}
            cierresNeedsAttention={cierresNeedsAttention}
          />
          <ScrollView
            {...webOperationalScrollBindings(cashStyles.turnoModeScroll)}
            contentContainerStyle={cashStyles.turnoModeContent}
            keyboardShouldPersistTaps="handled"
            refreshControl={cashRefreshControl}
          >
            <ChecksPortfolioSection
              flow={vm.checksFlow}
              branchId={vm.currentBranchId ?? null}
              canEdit={vm.canEditChecks}
              canEndorse={vm.canEndorseChecks}
              canReject={vm.canRejectChecks}
              expensesModuleEnabled={vm.expensesModuleEnabled}
            />
          </ScrollView>
        </ShellContentFrame>
      ) : (
        <ShellContentFrame style={{ width: '100%' }} testID="cash-historial">
          <CashModeChrome
            mode={screenMode}
            onModeChange={setScreenMode}
            readOnly={vm.canView && !vm.canEdit}
            showHistorial={vm.canView}
            showCartera={showCartera}
            cierresNeedsAttention={cierresNeedsAttention}
          />
          {historyList}
        </ShellContentFrame>
      )}

      <Modal
        visible={!useCashWorkspace && detailSession != null}
        animationType="slide"
        transparent
        onRequestClose={() => setDetailSession(null)}
        accessibilityViewIsModal
      >
        <View style={[posUi.modalBackdrop, { justifyContent: 'flex-end', padding: 0 }]}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={() => setDetailSession(null)}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <View style={[cashStyles.detailSheetContainer, { maxHeight: '92%', zIndex: 1 }]}>
            {detailSession ? (
              <CashSessionDetailPanel
                s={detailSession}
                onClose={() => setDetailSession(null)}
                branchId={vm.currentBranchId ?? null}
                operatorLabel={vm.resolveOperatorLabel}
              />
            ) : null}
          </View>
        </View>
      </Modal>

      <CashCustomRangeModal
        visible={customModalOpen}
        onClose={() => setCustomModalOpen(false)}
        customRangeFrom={vm.customRangeFrom}
        customRangeTo={vm.customRangeTo}
        setCustomRangeFrom={vm.setCustomRangeFrom}
        setCustomRangeTo={vm.setCustomRangeTo}
      />

      <FilterSheetModal
        visible={historialFiltersOpen}
        title="Filtros"
        appearance="ticket"
        onClose={() => setHistorialFiltersOpen(false)}
        onApply={() => setHistorialFiltersOpen(false)}
        onClear={() => {
          vm.setHistoryRangePreset('30d');
          vm.setDifferenceFilter('all');
          vm.setOperatorFilter('all');
          vm.setHistorySort('closed_desc');
        }}
        hasActiveDraft={
          vm.historyRangePreset !== '30d' ||
          vm.differenceFilter !== 'all' ||
          vm.operatorFilter !== 'all' ||
          vm.historySort !== 'closed_desc'
        }
      >
        <CashHistorialFiltersBlock vm={vm} onOpenCustomModal={onOpenCustomModal} filtersInModal />
      </FilterSheetModal>

      <CashOperationalSheetModal
        visible={lastClosedModalOpen}
        title="Último cierre"
        subtitle={
          vm.cashSummary?.lastClosedSession
            ? `${vm.cashSummary.lastClosedSession.closedAt ? formatRelativeDayTime(vm.cashSummary.lastClosedSession.closedAt) : '—'}${vm.lastClosedByLabel ? ' · Por ' + vm.lastClosedByLabel : ''}`
            : undefined
        }
        onClose={() => setLastClosedModalOpen(false)}
        footerLabel="Cerrar"
      >
        {vm.cashSummary?.lastClosedSession ? (
          <CashLastClosedSummary vm={vm} session={vm.cashSummary.lastClosedSession} />
        ) : (
          <Text style={styles.caption}>No hay cierres registrados.</Text>
        )}
      </CashOperationalSheetModal>

      {!useCashWorkspace ? (
        <>
          <CashOperationalSheetModal
            visible={outflowsModalOpen}
            title="Movimientos del turno"
            subtitle={
              vm.cashSummary?.activeSession
                ? `Egresado ${formatMoney(vm.cashSummary.sessionOutflowsTotal ?? 0)}${(vm.cashSummary.sessionInflowsTotal ?? 0) > 0 ? ` · Ingresado ${formatMoney(vm.cashSummary.sessionInflowsTotal ?? 0)}` : ''}`
                : undefined
            }
            onClose={() => setOutflowsModalOpen(false)}
            wide
          >
            {vm.cashSummary?.activeSession ? (
              <CashOutflowsSection
                {...outflowsSectionProps}
                embeddedInModal
              />
            ) : (
              <Text style={styles.caption}>No hay turno abierto.</Text>
            )}
          </CashOperationalSheetModal>
        </>
      ) : null}

      <CashOperationalSheetModal
        visible={branchActivityOpen}
        title="Actividad de sucursal"
        subtitle="Últimos 30 días · toda la sucursal"
        onClose={() => setBranchActivityOpen(false)}
        wide
      >
        <CashActivityFeed vm={vm.activityFlow} operatorLabel={vm.resolveOperatorLabel} layoutWide={layoutWide} />
      </CashOperationalSheetModal>

      <CashCloseTurnoModal
        visible={closeCashModalOpen}
        vm={vm}
        totalsLoading={closeCashPreparing}
        onClose={() => {
          setCloseCashModalOpen(false);
          setCloseCashPreparing(false);
        }}
      />

      {vm.expensesModuleEnabled && vm.outflowsFlow.registerDirection !== 'in' ? (
        <ExpenseRegisterModal
          visible={vm.outflowsFlow.registerOpen}
          variant="cashOutflow"
          submitting={vm.expenseRegisterSubmit.submitting}
          suppliers={vm.expenseRegisterSuppliers}
          branchId={vm.currentBranchId ?? null}
          activeCashSession={vm.activeCashSessionInfo}
          onClose={() => vm.outflowsFlow.setRegisterOpen(false)}
          onSubmit={vm.expenseRegisterSubmit.handleSubmit}
        />
      ) : (
        <CashOutflowRegisterModal
          visible={vm.outflowsFlow.registerOpen}
          direction={vm.outflowsFlow.registerDirection}
          submitting={vm.outflowsFlow.submitting}
          expectedCashInDrawer={vm.expectedCashInDrawer}
          onDirectionChange={vm.outflowsFlow.setRegisterDirection}
          onClose={() => vm.outflowsFlow.setRegisterOpen(false)}
          onSubmit={vm.outflowsFlow.handleRegister}
        />
      )}

      {vm.ConfirmModal}
      {vm.SupervisorPinModal}
    </>
  );
}
